import type { ThenableReference } from "firebase/database";
import {
  onDisconnect,
  onValue,
  push,
  remove,
  serverTimestamp,
  set,
  type DatabaseReference,
} from "firebase/database";
import invariant from "tiny-invariant";
import { createReference } from "../lib/firebase";
import { FirebaseStore } from "../lib/FirebaseStore";

/** Key in firebase */
const FIREBASE_KEY = "presence";

// TYPES
// -----

export interface PresenceData {
  [userId: string]: {
    /** The date the user was last online */
    lastSeen: number | null;

    /**
     * An array of dates, one for each connection, indicating when the connection was made.
     * If null, the user is not connected.
     */
    connections: Record<string, number> | null;
  };
}

export interface GroupedPresenceData {
  online: { userId: string; since: Date }[];
  offline: { userId: string; lastSeen: Date }[];
}

// PRESENCE
// --------

interface PresenceStoreOptions {
  eventPrefix: string;
}

export class PresenceStore extends FirebaseStore<PresenceData> {
  private _eventPrefix: string;
  private _connectedRef: DatabaseReference;

  constructor({ eventPrefix }: PresenceStoreOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY));

    // https://firebase.google.com/docs/database/web/offline-capabilities#section-connection-state
    this._connectedRef = createReference(".info/connected");
    this._eventPrefix = eventPrefix;
  }

  trackPresence(userId: string) {
    const connectionsRef = createReference(this._eventPrefix, FIREBASE_KEY, userId, "connections");
    const lastSeenRef = createReference(this._eventPrefix, FIREBASE_KEY, userId, "lastSeen");

    let currentConnectionRef: ThenableReference | null = null;

    const unsubscribe = onValue(this._connectedRef, (snapshot) => {
      if (snapshot.val() === true) {
        currentConnectionRef = push(connectionsRef, serverTimestamp());

        onDisconnect(currentConnectionRef).remove();
        onDisconnect(lastSeenRef).set(serverTimestamp());
      }
    });

    return () => {
      unsubscribe();
      if (currentConnectionRef) remove(currentConnectionRef);
      set(lastSeenRef, serverTimestamp());
    };
  }

  async getPresence(filter?: string[]) {
    const data = (await this.get()) ?? {};
    const presence: GroupedPresenceData = {
      online: [],
      offline: [],
    };

    for (const userId in data) {
      if (filter && !filter.includes(userId)) continue;

      const connections = Object.values(data[userId].connections ?? {});

      if (connections.length > 0) {
        const since = connections.reduce((acc, connection) => {
          const date = new Date(connection);
          return date.getTime() > acc.getTime() ? date : acc;
        }, new Date());

        presence.online.push({ userId, since });
      } else {
        invariant(data[userId].lastSeen, "lastSeen should not be null");

        presence.offline.push({
          userId,
          lastSeen: new Date(data[userId].lastSeen),
        });
      }
    }

    return presence;
  }

  subscribeToPresence(callback: (presence: GroupedPresenceData) => void, filter?: string[]) {
    return this.subscribe(async () => {
      callback(await this.getPresence(filter));
    });
  }
}
